import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import config from '../config';
import InstallApp from '../Components/InstallApp';
import {
    CreditCard,
    ArrowRight,
    AlertCircle,
    X,
    Hash,
    FileDigit,
    ShieldCheck,
    Smartphone,
    Mail,
    Loader2,
    Fingerprint
} from 'lucide-react';

export default function Login() {
    const [loginMethod, setLoginMethod] = useState('rut');
    const [inputValue, setInputValue] = useState('');
    const [run, setRun] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [isClosing, setIsClosing] = useState(false);

    const [isBioSupported, setIsBioSupported] = useState(false);
    const [isBioEnabledOnDevice, setIsBioEnabledOnDevice] = useState(false);
    const [bioLoading, setBioLoading] = useState(false);

    const [expressRut, setExpressRut] = useState('');
    const [expressClient, setExpressClient] = useState('');
    const [expressError, setExpressError] = useState('');

    const navigate = useNavigate();
    const currentYear = new Date().getFullYear();

    const base64URLToBuffer = (base64url) => {
        if (!base64url) return new Uint8Array(0);
        const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
        const pad = base64.length % 4;
        const padded = pad ? base64 + '='.repeat(4 - pad) : base64;
        const binStr = window.atob(padded);
        return Uint8Array.from(binStr, c => c.charCodeAt(0));
    };

    const bufferToBase64URL = (buffer) => {
        const bytes = new Uint8Array(buffer);
        let str = "";
        for (const charCode of bytes) str += String.fromCharCode(charCode);
        return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
    };

    useEffect(() => {
        const checkHardwareSupport = async () => {
            if (window.PublicKeyCredential) {
                const available = await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
                setIsBioSupported(available);
                const hasLocalStorageFlag = localStorage.getItem('biometric_enabled') === 'true';
                const hasDeviceId = localStorage.getItem('biometric_device_id');
                if (hasLocalStorageFlag && hasDeviceId) {
                    setIsBioEnabledOnDevice(true);
                }
            }
        };
        checkHardwareSupport();
    }, []);

    const checkRut = (rut) => {
        const value = rut.replace(/\./g, '').replace(/-/g, '');
        if (value.length < 2) return false;
        const body = value.slice(0, -1);
        const dv = value.slice(-1).toUpperCase();
        let suma = 0;
        let multiplo = 2;
        for (let i = 1; i <= body.length; i++) {
            const index = multiplo * value.charAt(body.length - i);
            suma = suma + index;
            if (multiplo < 7) { multiplo = multiplo + 1; } else { multiplo = 2; }
        }
        const dvEsperado = 11 - (suma % 11);
        let dvFinal = (dvEsperado === 11) ? "0" : (dvEsperado === 10) ? "K" : dvEsperado.toString();
        return dv === dvFinal;
    };

    const formatRut = (value) => {
        let cleanValue = value.replace(/[^0-9kK]/g, '');
        if (cleanValue.length > 9) cleanValue = cleanValue.slice(0, 9);
        const body = cleanValue.slice(0, -1);
        const dv = cleanValue.slice(-1).toUpperCase();
        if (cleanValue.length > 1) {
            let formattedBody = "";
            for (let i = body.length - 1, j = 1; i >= 0; i--, j++) {
                formattedBody = body.charAt(i) + formattedBody;
                if (j % 3 === 0 && i !== 0) formattedBody = "." + formattedBody;
            }
            return `${formattedBody}-${dv}`;
        }
        return cleanValue;
    };

    const handleInputChange = (e) => {
        const rawValue = e.target.value;
        setError('');
        if (loginMethod === 'rut') setInputValue(formatRut(rawValue));
        else if (loginMethod === 'client') setInputValue(rawValue.replace(/[^0-9-]/g, ''));
        else if (loginMethod === 'phone') setInputValue(rawValue.replace(/[^0-9]/g, '').slice(0, 9));
        else if (loginMethod === 'email') setInputValue(rawValue.trim());
    };

    const handleBiometricLogin = async () => {
        if (!isBioEnabledOnDevice) {
            setError('Primero debes habilitar la biometría desde tu sesión activa en "Mi Perfil".');
            return;
        }
        setBioLoading(true);
        setError('');
        try {
            const deviceId = localStorage.getItem('biometric_device_id');
            const startResponse = await fetch(`${config.apiBaseUrl}/api/sucursal-virtual/client-signin-biometric-start?device_id=${deviceId}`, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' }
            });
            if (!startResponse.ok) throw new Error("Error al obtener reto del servidor");
            const data = await startResponse.json();
            const publicKeyRequestOptions = {
                challenge: base64URLToBuffer(data.challenge),
                rpId: window.location.hostname,
                allowCredentials: [{
                    type: 'public-key',
                    id: base64URLToBuffer(deviceId),
                    transports: ['internal']
                }],
                userVerification: "required",
                timeout: 60000
            };
            const assertion = await window.navigator.credentials.get({
                publicKey: publicKeyRequestOptions
            });
            if (!assertion) throw new Error("Cancelado");
            const payload = {
                "eid": config.aprEid,
                "device_id": deviceId,
                "client_data_json": bufferToBase64URL(assertion.response.clientDataJSON),
                "authenticator_data": bufferToBase64URL(assertion.response.authenticatorData),
                "signature": bufferToBase64URL(assertion.response.signature),
                "run": run
            };
            const response = await fetch(`${config.apiBaseUrl}/api/sucursal-virtual/client-signin-biometric`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            const dataRes = await response.json();
            if (response.ok) {
                const expiresInMinutes = 15;
                const expirationTime = Date.now() + (expiresInMinutes * 60 * 1000);
                sessionStorage.setItem('token', dataRes.token);
                sessionStorage.setItem('user', JSON.stringify(dataRes.user));
                sessionStorage.setItem('session_expiry', expirationTime.toString());
                navigate('/inicio');
            } else {
                setError(dataRes.error || dataRes.message || 'Identidad biométrica no reconocida.');
            }
        } catch (err) {
            if (err.name === 'NotAllowedError') {
                setError('Acceso biométrico cancelado por el usuario.');
            } else {
                setError('Falla al activar el sensor biométrico del dispositivo.');
            }
        } finally {
            setBioLoading(false);
        }
    };

    const handleLogin = async (e) => {
        e.preventDefault();
        if (!inputValue) {
            setError('El campo no puede estar vacío');
            return;
        }
        if (loginMethod === 'rut' && !checkRut(inputValue)) {
            setError('El RUT ingresado no es válido');
            return;
        }
        setLoading(true);
        setError('');
        const payload = { eid: config.aprEid, run: run };
        if (loginMethod === 'rut') payload.rut = inputValue.replace(/\./g, '');
        else if (loginMethod === 'client') payload.nro_cliente = inputValue;
        else if (loginMethod === 'email') payload.email = inputValue;
        else if (loginMethod === 'phone') payload.phone = `+56${inputValue}`;
        try {
            const response = await fetch(`${config.apiBaseUrl}/api/sucursal-virtual/client-signin`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            const data = await response.json();
            if (response.ok) {
                navigate('/verification', { state: { phone: data.phone || data.masked_phone || '' } });
            } else {
                setError(data.message || 'Datos no válidos.');
            }
        } catch (err) {
            setError('Error de conexión.');
        } finally {
            setLoading(false);
        }
    };

    const handleExpressPayment = (e) => {
        e.preventDefault();
        setExpressError('');
        if (!expressClient) {
            setExpressError('Ingrese número de cliente');
            return;
        }
        if (!checkRut(expressRut)) {
            setExpressError('RUT ingresado no es válido');
            return;
        }

        const url = `${config.webpayUrl}?nrut=${expressRut}&ncliente=${expressClient}#no-back-button`;
        window.open(url, '_blank');
        handleCloseModal();
    };

    const handleCloseModal = () => {
        setIsClosing(true);
        setTimeout(() => {
            setShowModal(false);
            setIsClosing(false);
            setExpressRut('');
            setExpressClient('');
            setExpressError('');
        }, 300);
    };

    const switchTab = (method) => {
        setLoginMethod(method);
        setInputValue('');
        setError('');
    };

    const PoweredByFooter = ({ isDark = false, className = "" }) => (
        <div className={`flex flex-col items-center gap-1.5 ${className}`}>
            <span className={`text-[9px] font-bold uppercase tracking-widest text-center ${isDark ? 'text-sky-300/40' : 'text-slate-400'}`}>Powered by</span>
            <img className={isDark ? "image-invert-white" : ""} alt="Snap" src="/logo.snap.png" style={{height: '24px'}} />
            <div className={`text-[9px] text-center font-bold ${isDark ? 'text-sky-300/60' : 'text-slate-400'}`}>
                © {currentYear} {config.aprName}
            </div>
        </div>
    );

    return (
        <div className="min-h-screen w-full flex bg-slate-50 font-sans text-slate-600 relative overflow-hidden">
            {config.webpayUrl && showModal && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                    <div className={`absolute inset-0 bg-slate-900/60 backdrop-blur-sm ${isClosing ? 'opacity-0' : 'opacity-100'} transition-opacity duration-300`} onClick={handleCloseModal}></div>
                    <form onSubmit={handleExpressPayment} className={`relative w-full max-w-md bg-white rounded-3xl shadow-2xl p-8 transition-all duration-300 ${isClosing ? 'scale-95 opacity-0 translate-y-4' : 'scale-100 opacity-100 translate-y-0'}`}>
                        <button type="button" onClick={handleCloseModal} className="absolute top-4 right-4 p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full transition-colors"><X size={24} /></button>
                        <div className="text-center mb-6">
                            <div className="inline-flex items-center justify-center w-16 h-16 bg-sky-100 text-sky-600 rounded-2xl mb-4"><CreditCard size={32} /></div>
                            <h3 className="text-2xl font-bold text-slate-800">Pago Express</h3>
                            <p className="text-slate-500 mt-2 text-sm">Ingresa los datos para buscar tu deuda.</p>
                        </div>
                        {expressError && <div className="mb-4 bg-red-50 text-red-600 px-3 py-2 rounded-xl flex items-center gap-2 text-xs font-medium animate-pulse"><AlertCircle size={16} /> {expressError}</div>}
                        <div className="space-y-4">
                            <div>
                                <label className="text-sm font-bold text-slate-700 ml-1">N° Cliente</label>
                                <input type="text" inputMode="numeric" pattern="[0-9]*" value={expressClient} onChange={(e) => setExpressClient(e.target.value.replace(/\D/g, ''))} className="block w-full px-4 py-3 bg-slate-50 border-2 border-slate-200 rounded-xl font-bold outline-none focus:border-sky-500 transition-all" placeholder="Ej: 123456" />
                            </div>
                            <div>
                                <label className="text-sm font-bold text-slate-700 ml-1">RUT Titular</label>
                                <input type="text" value={expressRut} onChange={(e) => setExpressRut(formatRut(e.target.value))} className="block w-full px-4 py-3 bg-slate-50 border-2 border-slate-200 rounded-xl font-bold outline-none focus:border-sky-500 transition-all" placeholder="12.345.678-9" />
                            </div>
                            <button type="submit" disabled={!expressRut || !expressClient} className="w-full py-3.5 rounded-xl font-bold text-white bg-sky-600 hover:bg-sky-700 disabled:opacity-50 transition-all flex justify-center items-center gap-2">Buscar Deuda <ArrowRight size={18} /></button>
                        </div>
                    </form>
                </div>
            )}
            <div className="hidden lg:flex lg:w-1/2 relative flex-col justify-between overflow-hidden bg-sky-600">
                <div className="absolute inset-0">
                    <img
                        src="https://images.unsplash.com/photo-1518837695005-2083093ee35b?ixlib=rb-1.2.1&auto=format&fit=crop&w=1500&q=80"
                        className="w-full h-full object-cover opacity-50 mix-blend-overlay"
                        alt="bg"
                    />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-sky-900/90 via-sky-800/40 to-transparent"></div>
                <div className="relative z-10 flex flex-col justify-between h-full p-16 text-white w-full">
                    <div className="flex items-center gap-3"><img className="image-invert-white" style={{height:'120px'}} src={config.aprLogo} alt={config.aprName} /></div>
                    <div className="space-y-6">
                        <h1 className="text-5xl font-bold leading-tight uppercase">Acceso <span className="text-sky-300">Simple y Seguro.</span></h1>
                        <p className="text-lg text-sky-100 max-w-md">Ingresa con tus datos y recibe un código de acceso vía SMS al instante. Sin contraseñas que recordar.</p>
                        <div className="mt-8 p-6 bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 flex gap-4 items-center">
                            <div className="p-3 bg-emerald-500/20 rounded-full h-fit"><ShieldCheck className="text-emerald-300" size={24} /></div>
                            <div><h3 className="font-bold text-lg mb-1">Verificación por SMS</h3><p className="text-sm text-sky-100">Validamos tu identidad enviando un código único a tu celular en cada ingreso.</p></div>
                        </div>
                        {isBioSupported && (
                            <div className="p-6 bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 flex gap-4 items-center animate-in fade-in slide-in-from-bottom-4 duration-700">
                                <div className="p-3 bg-sky-400/20 rounded-full h-fit"><Fingerprint className="text-sky-300" size={24} /></div>
                                <div><h3 className="font-bold text-lg mb-1">Acceso Biométrico</h3><p className="text-sm text-sky-100">Si tu dispositivo lo permite, puedes usar tu huella o rostro para entrar más rápido.</p></div>
                            </div>
                        )}
                    </div>
                    <PoweredByFooter isDark={true} className="mt-8 pb-4" />
                </div>
            </div>
            <div className="w-full lg:w-1/2 flex items-center justify-center p-4 md:p-6 lg:p-12 overflow-y-auto">
                <div className="w-full max-w-md flex flex-col min-h-full py-6">
                    <div className="flex-1 space-y-6 md:space-y-8">
                        {isBioSupported && <InstallApp />}
                        <div className="lg:hidden flex justify-center mb-4 md:mb-8"><img style={{height:'80px'}} src={config.aprLogo} alt={config.aprName} /></div>
                        <div className="text-center lg:text-left"><h2 className="text-2xl md:text-3xl font-bold text-slate-800">¡Bienvenido! 👋</h2><p className="mt-1 md:mt-2 text-slate-500 text-sm">Selecciona cómo deseas ingresar.</p></div>
                        {config.webpayUrl && (
                            <>
                                <div onClick={() => setShowModal(true)} className="bg-sky-50 border border-sky-100 rounded-2xl p-4 md:p-5 flex items-center justify-between transition-all hover:border-sky-300 hover:shadow-md cursor-pointer group hover:bg-sky-100/50">
                                    <div className="flex items-center gap-3 md:gap-4">
                                        <div className="bg-white p-2.5 md:p-3 rounded-xl text-sky-600 shadow-sm group-hover:scale-110 transition-transform"><CreditCard size={20} className="md:w-6 md:h-6" /></div>
                                        <div className="text-left"><h3 className="font-bold text-slate-800 group-hover:text-sky-700 text-sm md:text-base">Pago Express</h3><p className="text-[10px] md:text-xs text-slate-500">Paga cuenta sin clave</p></div>
                                    </div>
                                    <ArrowRight size={18} className="text-sky-400 group-hover:translate-x-1 transition-transform md:w-5 md:h-5" />
                                </div>
                                <div className="relative"><div className="absolute inset-0 flex items-center"><span className="w-full border-t border-slate-200" /></div><div className="relative flex justify-center text-[10px] uppercase"><span className="bg-slate-50 px-2 text-slate-400 font-medium">O ingresa a tu cuenta</span></div></div>
                            </>
                        )}
                        {isBioSupported && (
                            <button onClick={handleBiometricLogin} disabled={bioLoading || loading} className="w-full py-5 bg-emerald-50 border-2 border-emerald-100 rounded-2xl font-bold text-emerald-700 flex items-center justify-center gap-3 hover:bg-emerald-100 transition-all active:scale-[0.98] shadow-sm shadow-emerald-100/50 animate-in fade-in zoom-in-95 duration-500">
                                {bioLoading ? <Loader2 className="animate-spin" size={24} /> : <Fingerprint size={24} />} Ingresar con mi Huella
                            </button>
                        )}
                        <form className="space-y-4 md:space-y-6" onSubmit={handleLogin}>
                            <div className="hidden" aria-hidden="true">
                                <input type="text" name="run" value={run} onChange={(e) => setRun(e.target.value)} tabIndex="-1" autoComplete="off" />
                            </div>
                            <div className="grid grid-cols-4 p-1 bg-slate-100 rounded-xl gap-0.5 md:gap-1">
                                <button type="button" onClick={() => switchTab('rut')} className={`py-2 text-[10px] md:text-sm font-bold rounded-lg transition-all ${loginMethod === 'rut' ? 'bg-white text-sky-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>RUT</button>
                                <button type="button" onClick={() => switchTab('client')} className={`py-2 text-[10px] md:text-sm font-bold rounded-lg transition-all ${loginMethod === 'client' ? 'bg-white text-sky-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>Cliente</button>
                                <button type="button" onClick={() => switchTab('email')} className={`py-2 text-[10px] md:text-sm font-bold rounded-lg transition-all ${loginMethod === 'email' ? 'bg-white text-sky-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>Email</button>
                                <button type="button" onClick={() => switchTab('phone')} className={`py-2 text-[10px] md:text-sm font-bold rounded-lg transition-all ${loginMethod === 'phone' ? 'bg-white text-sky-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>Celular</button>
                            </div>
                            {error && <div className="bg-red-50 text-red-600 px-3 py-2 rounded-xl flex items-center gap-2 text-xs font-medium animate-pulse"><AlertCircle size={16} /> {error}</div>}
                            <div className="space-y-1.5 md:space-y-2">
                                <label className="text-xs md:text-sm font-bold text-slate-700 ml-1 uppercase tracking-wider">{loginMethod === 'rut' ? "RUT del Titular" : loginMethod === 'client' ? "N° de Cliente" : loginMethod === 'email' ? "Email" : "Celular"}</label>
                                <div className="relative group">
                                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-400">
                                        {loginMethod === 'rut' && <FileDigit size={18} />}{loginMethod === 'client' && <Hash size={18} />}{loginMethod === 'email' && <Mail size={18} />}{loginMethod === 'phone' && <Smartphone size={18} />}
                                    </div>
                                    <input disabled={loading || bioLoading} type={loginMethod === 'email' ? "email" : "text"} inputMode={(loginMethod === 'client' || loginMethod === 'phone') ? "numeric" : "text"} pattern={(loginMethod === 'client' || loginMethod === 'phone') ? "[0-9]*" : undefined} value={inputValue} onChange={handleInputChange} className="block w-full pl-11 pr-4 py-3.5 bg-white border-2 border-slate-100 rounded-xl text-slate-700 font-medium text-lg outline-none focus:border-sky-500 transition-all shadow-sm" placeholder={loginMethod === 'rut' ? "12.345.678-9" : loginMethod === 'client' ? "Ej: 123456" : loginMethod === 'email' ? "tu@correo.com" : "9 1234 5678"} />
                                </div>
                            </div>
                            <button type="submit" disabled={loading || bioLoading} className="w-full flex justify-center py-4 px-4 rounded-xl text-lg font-bold text-white bg-sky-600 hover:bg-sky-700 shadow-lg shadow-sky-200 transition-all active:scale-[0.98] disabled:opacity-70">
                                {loading ? <Loader2 className="animate-spin" size={24} /> : "Continuar"}
                            </button>
                        </form>
                        <div className="pt-2 text-center pb-6"><p className="text-xs md:text-sm text-slate-500 mb-4">¿Aún no tienes cuenta? <button type="button" onClick={() => navigate('/signup')} className="font-bold text-sky-600 hover:text-sky-700 hover:underline">Regístrate aquí</button></p></div>
                    </div>
                    <div className="lg:hidden mt-auto pt-8 pb-24"><PoweredByFooter isDark={false} /></div>
                </div>
            </div>
        </div>
    );
}
